import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  afterRenderEffect,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { auditTime, filter, fromEvent, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { EventqAiEventTag, type EventqAiEventTagVariant } from '../ai-event-tag/ai-event-tag';
import { EventqAlarmIndicator } from '../alarm-indicator/alarm-indicator';
import { EventqFlagAlarmToggle } from '../flag-alarm-toggle/flag-alarm-toggle';
import {
  EventqDataTableAppearance,
  EventqDataTableCellClick,
  EventqDataTableCellAction,
  EventqDataTableColumn,
  EventqDataTableDensity,
  EventqDataTableRow,
  EventqSortDirection,
  EventqSortState,
} from './data-table.types';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';

export type {
  EventqDataTableAppearance,
  EventqDataTableCellClick,
  EventqDataTableCellAction,
  EventqDataTableCellType,
  EventqDataTableColumn,
  EventqDataTableDensity,
  EventqDataTableRow,
  EventqSortDirection,
  EventqSortState,
} from './data-table.types';

const DEFAULT_SKELETON_COUNT = 5;
/**
 * Definite width for a content-sized wrap column.
 * `minmax()` in this custom property is parsed as more than one track and
 * collapses the row into a single column.
 */
const WRAP_COLUMN_TRACK = '24rem';
/** Lists longer than this, in a scrolling table, render a window instead of every row. */
const VIRTUAL_ROW_THRESHOLD = 60;
const VIRTUAL_OVERSCAN_ROWS = 8;
/**
 * Used until the first row is measured. Matches `--eventq-data-table-row-height`.
 * The row border is inside that height (`box-sizing: border-box`).
 */
const FALLBACK_ROW_STRIDE: Record<'default' | 'compact', number> = { default: 40, compact: 34 };
const FALLBACK_VIEWPORT_ROWS = 20;

/** Latest scroll offset is emitted at most once per this window. */
const DATA_TABLE_SCROLL_AUDIT_MS = 100;

const AI_EVENT_TAG_VARIANTS: readonly EventqAiEventTagVariant[] = [
  'motion',
  'human',
  'vehicle',
  'gun',
];

function isAiEventTagVariant(value: unknown): value is EventqAiEventTagVariant {
  return typeof value === 'string' && (AI_EVENT_TAG_VARIANTS as readonly string[]).includes(value);
}

@Component({
  selector: 'eventq-data-table',
  imports: [
    NgTemplateOutlet,
    MatIcon,
    EventqAlarmIndicator,
    EventqAiEventTag,
    EventqFlagAlarmToggle,
    PrefixUrlPipe,
  ],
  templateUrl: './data-table.html',
  styleUrl: './data-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.eventq-data-table-host--fill]': 'fillRemainingHeight()',
    '[class.eventq-data-table-host--fit-content]': 'fitsContent()',
    '[class.eventq-data-table-host--resolve]': "appearance() === 'resolve'",
    '[class.eventq-data-table-host--row-static]': '!rowInteractive()',
    '[attr.aria-busy]': "loading() ? 'true' : 'false'",
  },
})
export class EventqDataTable {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bodyRef = viewChild<ElementRef<HTMLElement>>('tableBody');
  private readonly scrollTop = signal(0);
  private readonly viewportHeight = signal(0);
  private readonly measuredRowStride = signal(0);
  private readonly measuredDensity = signal<EventqDataTableDensity | null>(null);
  /**
   * Registered before `scrollChange` so destroy emits while output listeners
   * still exist. Output emitters clear their listeners on destroy.
   */
  private readonly flushScrollOnDestroy = this.destroyRef.onDestroy(() => this.flushScroll());
  /** True while `restoreScrollOffset` assigns `scrollTop`, so that write is not a user scroll. */
  private ignoringRestoreScroll = false;
  /** Offset to apply once the body can scroll. Cleared when the user scrolls. */
  private pendingRestoreOffset: number | null = null;
  private rawScrollTop = 0;
  private hasUserScroll = false;
  private lastEmittedScroll: number | null = null;

  readonly columns = input.required<EventqDataTableColumn[]>();
  readonly rows = input.required<EventqDataTableRow[]>();
  readonly sort = input<EventqSortState | null>(null);
  readonly maxHeight = input<string | null>(null);
  readonly fillRemainingHeight = input(false);
  readonly density = input<EventqDataTableDensity>('default');
  /**
   * Table chrome theme. `'resolve'` is reserved for a future Resolve look;
   * only `'default'` is styled today.
   */
  readonly appearance = input<EventqDataTableAppearance>('default');
  readonly selectedRowId = input<string | null>(null);
  /** When true and rows are empty (and not loading), render emptyLabel (+ optional icon). */
  readonly showLabelWhenEmpty = input(false);
  readonly emptyLabel = input('No Alarms in this site.');
  readonly emptyIconSrc = input<string | null>(null);
  /**
   * When true, replaces the body with skeleton rows. Header stays visible.
   * Sort and row interactions are disabled while loading.
   */
  readonly loading = input(false);
  /** Skeleton row count while loading. Defaults to 5. */
  readonly skeletonCount = input<number | null>(null);
  /**
   * When true, `armStatus` cells are clickable buttons (Admin/Manager/Supervisor).
   * When false, icons are display-only.
   */
  readonly armStatusInteractive = input(false);
  /** When false, rows are display-only; use `cellType: 'link'` + `cellClick` for cell actions. */
  readonly rowInteractive = input(true);
  /**
   * When false, Space on a row does not activate it (hosts can bind Space to another action).
   * Enter still activates when `rowInteractive` is true.
   */
  readonly rowSpaceActivates = input(true);
  /**
   * Row ids that are visible but not openable — dimmed, unfocusable, and silent on click.
   * Use for rows the current user may read but not act on (e.g. work owned by someone else).
   */
  readonly disabledRowIds = input<readonly string[]>([]);
  /**
   * Size each column to its widest rendered cell (and header), and let the
   * table grow past the panel. Off while loading or when there are no rows.
   */
  readonly sizeColumnsToContent = input(false, { transform: booleanAttribute });

  readonly sortChange = output<EventqSortState>();
  /** Body `scrollTop`, at most once per {@link DATA_TABLE_SCROLL_AUDIT_MS}. */
  readonly scrollChange = output<number>();
  readonly rowClick = output<EventqDataTableRow>();
  readonly cellClick = output<EventqDataTableCellClick>();
  readonly flagChange = output<{ rowId: string; flagged: boolean }>();
  readonly armStatusClick = output<{ row: EventqDataTableRow; anchor: HTMLElement }>();
  readonly cellAction = output<EventqDataTableCellAction>();

  readonly isEmpty = computed(() => this.rows().length === 0);
  /** Content tracks only for a loaded page of rows. Skeletons and empty states stay fluid. */
  readonly fitsContent = computed(
    () => this.sizeColumnsToContent() && !this.loading() && this.rows().length > 0,
  );
  private readonly disabledRowIdSet = computed(() => new Set(this.disabledRowIds()));
  readonly skeletonIndexes = computed(() => {
    const explicit = this.skeletonCount();
    const count = explicit != null && explicit > 0 ? explicit : DEFAULT_SKELETON_COUNT;
    return Array.from({ length: count }, (_, index) => index);
  });
  /** Wrapped rows are variable height, so a long list cannot be windowed. */
  readonly hasWrapColumn = computed(() => this.columns().some((column) => column.wrap));
  readonly gridTemplateColumns = computed(() =>
    this.columns()
      .map((column) => {
        if (column.width) {
          return column.width;
        }
        if (column.wrap && this.fitsContent()) {
          return WRAP_COLUMN_TRACK;
        }
        if (this.fitsContent()) {
          return 'max-content';
        }
        const grow = column.grow ?? 1;
        return `minmax(0, ${grow}fr)`;
      })
      .join(' '),
  );

  /**
   * A scrolling table with a long list only mounts the rows in view.
   * Content-sized tables still render every row so column measurement stays correct.
   * Wrap columns do too: their rows are not a fixed height.
   */
  readonly shouldVirtualize = computed(
    () =>
      !this.loading() &&
      !this.fitsContent() &&
      !this.hasWrapColumn() &&
      (this.fillRemainingHeight() || !!this.maxHeight()) &&
      this.rows().length > VIRTUAL_ROW_THRESHOLD,
  );

  private readonly virtualRange = computed(() => {
    const count = this.rows().length;
    const stride = this.rowStridePx();
    const viewport =
      this.viewportHeight() > 0 ? this.viewportHeight() : stride * FALLBACK_VIEWPORT_ROWS;
    const visible = Math.ceil(viewport / stride) + VIRTUAL_OVERSCAN_ROWS * 2;
    let start = Math.max(0, Math.floor(this.scrollTop() / stride) - VIRTUAL_OVERSCAN_ROWS);
    if (start >= count) {
      start = Math.max(0, count - visible);
    }
    const end = Math.min(count, start + visible);
    return { start, end, offset: start * stride, total: count * stride };
  });

  readonly renderedRows = computed(() => {
    const rows = this.rows();
    if (!this.shouldVirtualize()) {
      return rows;
    }
    const { start, end } = this.virtualRange();
    return rows.slice(start, end);
  });

  readonly virtualHeight = computed(() => this.virtualRange().total);
  readonly virtualTransform = computed(() => `translateY(${this.virtualRange().offset}px)`);

  constructor() {
    afterNextRender(() => {
      this.bindScrollAudit();
      const body = this.bodyRef()?.nativeElement;
      if (!body || typeof ResizeObserver === 'undefined') {
        return;
      }
      const observer = new ResizeObserver(() => this.syncVirtualViewport(body));
      observer.observe(body);
      this.syncVirtualViewport(body);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });

    afterRenderEffect(() => {
      this.rows();
      this.loading();
      untracked(() => this.applyPendingRestore());
    });

    // Skeletons and the first data rows can share the same viewport height, so
    // ResizeObserver never fires for that swap. Measure after the rows paint.
    afterRenderEffect(() => {
      const density = this.density();
      if (!this.shouldVirtualize()) {
        return;
      }
      if (this.measuredRowStride() > 0 && this.measuredDensity() === density) {
        return;
      }
      const row = this.bodyRef()?.nativeElement.querySelector('.eventq-data-table__row');
      if (row instanceof HTMLElement && row.offsetHeight > 0) {
        this.measuredRowStride.set(row.offsetHeight);
        this.measuredDensity.set(density);
      }
    });
  }

  onBodyScroll(event: Event): void {
    if (!this.shouldVirtualize()) {
      return;
    }
    const next = (event.target as HTMLElement).scrollTop;
    const stride = this.rowStridePx();
    if (Math.floor(next / stride) === Math.floor(this.scrollTop() / stride)) {
      return;
    }
    this.scrollTop.set(next);
  }

  private syncVirtualViewport(body: HTMLElement): void {
    const height = body.clientHeight;
    if (height > 0 && height !== this.viewportHeight()) {
      this.viewportHeight.set(height);
    }
    this.measureFirstRow(body);
  }

  private measureFirstRow(body: HTMLElement): void {
    if (this.measuredRowStride() > 0 && this.measuredDensity() === this.density()) {
      return;
    }
    const row = body.querySelector('.eventq-data-table__row');
    if (row instanceof HTMLElement && row.offsetHeight > 0) {
      this.measuredRowStride.set(row.offsetHeight);
      this.measuredDensity.set(this.density());
    }
  }

  private rowStridePx(): number {
    const measured = this.measuredRowStride();
    if (measured > 0 && this.measuredDensity() === this.density()) {
      return measured;
    }
    return FALLBACK_ROW_STRIDE[this.density()];
  }

  onHeaderClick(column: EventqDataTableColumn): void {
    if (this.loading() || !column.sortable) {
      return;
    }

    const current = this.sort();
    let direction: EventqSortDirection = 'asc';

    if (current?.column === column.key) {
      direction = current.direction === 'asc' ? 'desc' : 'asc';
    }

    this.sortChange.emit({ column: column.key, direction });
  }

  onRowActivate(row: EventqDataTableRow): void {
    if (!this.rowInteractive() || this.loading() || this.isRowDisabled(row)) {
      return;
    }
    this.rowClick.emit(row);
  }

  onRowSpace(event: Event, row: EventqDataTableRow): void {
    if (!this.rowSpaceActivates()) {
      return;
    }
    event.preventDefault();
    this.onRowActivate(row);
  }

  isRowDisabled(row: EventqDataTableRow): boolean {
    return this.disabledRowIdSet().has(this.rowId(row));
  }

  onLinkCellClick(event: Event, row: EventqDataTableRow, column: EventqDataTableColumn): void {
    event.stopPropagation();
    if (this.loading()) {
      return;
    }
    this.cellClick.emit({ row, columnKey: column.key });
  }

  sortIcon(column: EventqDataTableColumn): string | null {
    if (!column.sortable) {
      return null;
    }

    const current = this.sort();
    if (!current || current.column !== column.key) {
      return 'swap_vert';
    }
    return current.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  cellValue(row: EventqDataTableRow, key: string): string {
    const value = row[key];
    return value === undefined || value === null ? '' : String(value);
  }

  rowId(row: EventqDataTableRow): string {
    return String(row['id'] ?? '');
  }

  cellType(column: EventqDataTableColumn): string {
    return column.cellType ?? 'text';
  }

  cellAlign(column: EventqDataTableColumn): 'start' | 'center' {
    return column.align ?? 'start';
  }

  aiTags(
    row: EventqDataTableRow,
    key: string,
  ): { label: string; variant: EventqAiEventTagVariant }[] {
    const value = row[key];
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter(
      (tag): tag is { label: string; variant: EventqAiEventTagVariant } =>
        !!tag &&
        typeof tag === 'object' &&
        typeof (tag as { label?: unknown }).label === 'string' &&
        isAiEventTagVariant((tag as { variant?: unknown }).variant),
    );
  }

  tagTrackKey(tag: { label: string; variant: string }, index: number): string {
    return `${index}:${tag.label}`;
  }

  indicatorActive(row: EventqDataTableRow, key: string): boolean {
    const value = row[key];
    return value === true || (typeof value === 'string' && value !== 'none' && value !== '');
  }

  showIndicator(row: EventqDataTableRow, column: EventqDataTableColumn): boolean {
    if (!column.indicatorKey) {
      return true;
    }
    return this.indicatorActive(row, column.indicatorKey);
  }

  isFlagged(row: EventqDataTableRow, key: string): boolean {
    return Boolean(row[key]);
  }

  onFlagToggle(row: EventqDataTableRow, key: string, flagged: boolean): void {
    if (this.loading()) {
      return;
    }
    this.flagChange.emit({ rowId: this.rowId(row), flagged });
  }

  armStatus(row: EventqDataTableRow, key: string): 'armed' | 'disarmed' {
    return row[key] === 'disarmed' ? 'disarmed' : 'armed';
  }

  /** Same Material marks as armed (`verified_user`) and disarmed (`security`) sites. */
  armStatusIcon(row: EventqDataTableRow, key: string): 'verified_user' | 'security' {
    return this.armStatus(row, key) === 'disarmed' ? 'security' : 'verified_user';
  }

  armStatusLabel(row: EventqDataTableRow, key: string): string {
    return this.armStatus(row, key) === 'disarmed' ? 'Disarmed' : 'Armed';
  }

  onArmStatusClick(event: MouseEvent, row: EventqDataTableRow): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.loading() || !this.armStatusInteractive()) {
      return;
    }
    const anchor = event.currentTarget;
    if (!(anchor instanceof HTMLElement)) {
      return;
    }
    this.armStatusClick.emit({ row, anchor });
  }

  onCellAction(event: MouseEvent, row: EventqDataTableRow, column: EventqDataTableColumn): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.loading()) {
      return;
    }
    const anchor = event.currentTarget;
    if (!(anchor instanceof HTMLElement)) {
      return;
    }
    this.cellAction.emit({ row, column, anchor });
  }

  /** Current body scroll offset. `0` when the body is not in the DOM. */
  scrollOffset(): number {
    return this.scrollBody()?.scrollTop ?? 0;
  }

  /**
   * Moves the body to `offset`. A scroll event caused by this write is ignored.
   * If the rows are still loading or the body cannot scroll yet, the offset is
   * applied on the next render that has rows. A user scroll cancels that pending apply.
   */
  restoreScrollOffset(offset: number): void {
    this.pendingRestoreOffset = offset;
    this.applyPendingRestore();
  }

  private bindScrollAudit(): void {
    const body = this.scrollBody();
    if (!body) {
      return;
    }

    fromEvent(body, 'scroll')
      .pipe(
        filter(() => !this.ignoringRestoreScroll),
        tap(() => {
          this.rawScrollTop = this.scrollOffset();
          this.hasUserScroll = true;
          this.pendingRestoreOffset = null;
        }),
        auditTime(DATA_TABLE_SCROLL_AUDIT_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.emitScroll(this.rawScrollTop));
  }

  private applyPendingRestore(): void {
    const target = this.pendingRestoreOffset;
    if (target == null || this.loading() || this.rows().length === 0) {
      return;
    }

    const body = this.scrollBody();
    if (!body) {
      return;
    }

    this.ignoringRestoreScroll = true;
    body.scrollTop = target;
    this.ignoringRestoreScroll = false;

    const applied = body.scrollTop;
    const max = Math.max(0, body.scrollHeight - body.clientHeight);
    const contentNotReady = max === 0 && target > 0 && applied !== target;
    if (contentNotReady) {
      return;
    }

    // The rendered window follows this signal. Native scroll updates it through
    // onBodyScroll; a scrollTop assignment does not emit scroll in jsdom.
    if (this.shouldVirtualize()) {
      this.scrollTop.set(applied);
    }

    this.pendingRestoreOffset = null;
    if (applied !== target) {
      this.rawScrollTop = applied;
      this.hasUserScroll = true;
      this.emitScroll(applied);
    }
  }

  private emitScroll(offset: number): void {
    this.lastEmittedScroll = offset;
    this.scrollChange.emit(offset);
  }

  /** Destroy bypasses the audit window so a view switch keeps the latest offset. */
  private flushScroll(): void {
    if (!this.hasUserScroll || this.rawScrollTop === this.lastEmittedScroll) {
      return;
    }
    this.scrollChange.emit(this.rawScrollTop);
    this.lastEmittedScroll = this.rawScrollTop;
  }

  private scrollBody(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.eventq-data-table__body');
  }

  /** Moves keyboard focus to the row and scrolls it into view. */
  focusRow(rowId: string): void {
    const id = rowId.trim();
    if (!id) {
      return;
    }
    this.revealRow(id);
    if (this.focusRenderedRow(id)) {
      return;
    }
    afterNextRender(() => this.focusRenderedRow(id), { injector: this.injector });
  }

  /** Scrolls a virtualized row into the mounted window before it can be focused. */
  private revealRow(id: string): void {
    if (!this.shouldVirtualize()) {
      return;
    }
    if (this.renderedRows().some((row) => this.rowId(row) === id)) {
      return;
    }
    const index = this.rows().findIndex((row) => this.rowId(row) === id);
    if (index < 0) {
      return;
    }
    const top = index * this.rowStridePx();
    this.scrollTop.set(top);
    const body = this.bodyRef()?.nativeElement;
    if (body) {
      body.scrollTop = top;
    }
  }

  private focusRenderedRow(id: string): boolean {
    const rows = this.host.nativeElement.querySelectorAll('.eventq-data-table__row');
    for (const candidate of Array.from(rows)) {
      if (candidate instanceof HTMLElement && candidate.getAttribute('data-row-id') === id) {
        candidate.focus({ preventScroll: true });
        candidate.scrollIntoView?.({ block: 'nearest' });
        return true;
      }
    }
    return false;
  }
}
